/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { Toast } from '@coze-arch/coze-design';
import { type GetDraftBotInfoAgwData } from '@coze-arch/idl/playground_api';

import { type ModelInfoConfig, useModelStore } from './model';
import { type InferenceMode, useInferenceModeStore } from './inference-mode';
import { useDocumentWorkspaceStore } from './document-workspace';

export type ChatSessionSwitchStatus = 'idle' | 'saving';

const SAVE_FAILED_TOAST = '保存推理配置失败，请稍后重试';

export interface SwitchInferenceConfigPatch {
  mode?: InferenceMode;
  workspace?: { workspaceId?: string; workspaceName?: string };
  ontologyApp?: { ontology_app_id?: string; ontology_app_name?: string };
}

interface SessionRelevantSnapshot {
  mode: InferenceMode;
  workspaceId: string;
  ontologyAppId: string;
}

type RollbackSnapshot = SessionRelevantSnapshot & {
  workspaceName?: string;
  modelConfig: ModelInfoConfig;
};

const readSessionRelevant = (): SessionRelevantSnapshot => {
  const { mode } = useInferenceModeStore.getState();
  const workspaceId =
    useDocumentWorkspaceStore.getState().workspaceId?.trim() ?? '';
  const config = useModelStore.getState().config as ModelInfoConfig;
  const ontologyAppId = config.ontology_app_id?.trim() ?? '';
  return { mode, workspaceId, ontologyAppId };
};

const sessionRelevantEquals = (
  a: SessionRelevantSnapshot,
  b: SessionRelevantSnapshot,
): boolean =>
  a.mode === b.mode &&
  a.workspaceId === b.workspaceId &&
  a.ontologyAppId === b.ontologyAppId;

const captureRollbackSnapshot = (): RollbackSnapshot => {
  const { workspaceName } = useDocumentWorkspaceStore.getState();
  return {
    ...readSessionRelevant(),
    workspaceName,
    modelConfig: {
      ...(useModelStore.getState().config as ModelInfoConfig),
    },
  };
};

const restoreRollbackSnapshot = (snapshot: RollbackSnapshot) => {
  useInferenceModeStore.getState().setInferenceMode({ mode: snapshot.mode });
  useDocumentWorkspaceStore.getState().setDocumentWorkspace({
    workspaceId: snapshot.workspaceId || undefined,
    workspaceName: snapshot.workspaceName,
  });
  useModelStore.getState().setModelByImmer(draft => {
    draft.config = { ...snapshot.modelConfig };
  });
};

const applySwitchPatch = (patch: SwitchInferenceConfigPatch) => {
  if (patch.mode !== undefined) {
    useInferenceModeStore.getState().setInferenceMode({ mode: patch.mode });
  }
  if (patch.workspace !== undefined) {
    useDocumentWorkspaceStore.getState().setDocumentWorkspace(patch.workspace);
  }
  const { ontologyApp } = patch;
  if (ontologyApp !== undefined) {
    useModelStore.getState().setModelByImmer(draft => {
      draft.config = {
        ...draft.config,
        model_source: 'ontology',
        ontology_app_id: ontologyApp.ontology_app_id,
        ontology_app_name: ontologyApp.ontology_app_name,
      };
    });
  }
};

let switchGeneration = 0;
/** 一轮连点开始前、上次已落库的配置。失败时整段退回这里，而不是退到中间某一次点击。 */
let inflightBaseline: RollbackSnapshot | null = null;

const resetSwitchRuntime = () => {
  switchGeneration = 0;
  inflightBaseline = null;
};

const notifySwitchFailed = () => {
  Toast.error({
    content: SAVE_FAILED_TOAST,
    duration: 3,
    theme: 'light',
    showClose: false,
  });
};

export interface ChatSessionStore {
  sessionEpoch: number;
  /**
   * 已执行过会话重置的 epoch。聊天区可能被上层整体卸载重挂（如 Coze 刷新消息列表时
   * initStatus=loading 会让编辑器 return null），重置必须按 epoch 只执行一次。
   */
  resetConsumedEpoch: number;
  switchStatus: ChatSessionSwitchStatus;
}

export const getDefaultChatSessionStore = (): ChatSessionStore => ({
  sessionEpoch: 0,
  resetConsumedEpoch: 0,
  switchStatus: 'idle',
});

export interface ChatSessionAction {
  switchInferenceConfig: (patch: SwitchInferenceConfigPatch) => Promise<void>;
  markResetConsumed: (epoch: number) => void;
  initStore: (_botData: GetDraftBotInfoAgwData) => void;
  clear: () => void;
}

export const useChatSessionStore = create<
  ChatSessionStore & ChatSessionAction
>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...getDefaultChatSessionStore(),
      switchInferenceConfig: async patch => {
        const before = readSessionRelevant();
        const ownsBaseline = inflightBaseline === null;
        if (ownsBaseline) {
          inflightBaseline = captureRollbackSnapshot();
        }
        applySwitchPatch(patch);
        const after = readSessionRelevant();
        if (sessionRelevantEquals(before, after)) {
          if (ownsBaseline) {
            inflightBaseline = null;
          }
          return;
        }

        const myGen = ++switchGeneration;
        set({ switchStatus: 'saving' });

        const { autosaveManager } = await import(
          '@/save-manager/auto-save/index'
        );

        try {
          await autosaveManager.flushInferenceConfigAsync();
        } catch (error) {
          // 只有最后一次切换负责回滚；更早的请求失败时界面已经在往更新的模式走
          if (myGen === switchGeneration) {
            if (inflightBaseline) {
              restoreRollbackSnapshot(inflightBaseline);
            }
            inflightBaseline = null;
            set({ switchStatus: 'idle' });
            notifySwitchFailed();
          }
          if (IS_DEV_MODE) {
            console.warn(
              '[chat-session] switchInferenceConfig flush failed',
              error,
            );
          }
          return;
        }

        if (myGen !== switchGeneration) {
          return;
        }

        inflightBaseline = null;
        set({
          sessionEpoch: get().sessionEpoch + 1,
          switchStatus: 'idle',
        });
      },
      markResetConsumed: epoch => {
        if (epoch > get().resetConsumedEpoch) {
          set({ resetConsumedEpoch: epoch });
        }
      },
      initStore: () => {
        resetSwitchRuntime();
        set({ ...getDefaultChatSessionStore() });
      },
      clear: () => {
        resetSwitchRuntime();
        set({ ...getDefaultChatSessionStore() });
      },
    })),
    {
      enabled: IS_DEV_MODE,
      name: 'botStudio.botDetail.chatSession',
    },
  ),
);
